import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import NewUserButton from './NewUserButton';
import UserRow from './UserRow';
import Pagination from '../../components/Pagination';
import { getUsers, deleteUser, startUser, stopUser, getPredadorStatus, encerrarDolarizar, retomarPredador, markFundPaid } from '../../services/UsersService';
import { createInviteCode, getAllInviteCodes, deleteInviteCode } from '../../services/InviteCodesService';
import Toast from '../../components/Toast';
import SearchUser from './SearchUser';
import UserModal from './UserModal/UserModal';

function Users() {

    const defaultLocation = useLocation();

    const [users, setUsers] = useState([]);

    const [editUser, setEditUser] = useState({});

    const [count, setCount] = useState(0);

    const [search, setSearch] = useState('');

    const [page, setPage] = useState(getPage());

    const [notification, setNotification] = useState({ type: '', text: '' });
    const [showInviteCodes, setShowInviteCodes] = useState(false);
    const [inviteCodes, setInviteCodes] = useState([]);
    const [inviteCodesCount, setInviteCodesCount] = useState(0);
    const [inviteCodesPage, setInviteCodesPage] = useState(1);
    const [generatingCode, setGeneratingCode] = useState(false);
    const [predadorPaused, setPredadorPaused] = useState(false);
    const [btcSignal, setBtcSignal] = useState(null);
    const [custodyCount, setCustodyCount] = useState(0);
    const [dollarizing, setDollarizing] = useState(false);
    const [resumingPredador, setResumingPredador] = useState(false);
    const [payingFundId, setPayingFundId] = useState('');

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    useEffect(() => {
        loadPredadorStatus();
    }, []);

    function loadPredadorStatus() {
        getPredadorStatus()
            .then(result => {
                setPredadorPaused(Boolean(result?.paused));
                setBtcSignal(result?.btc || null);
                setCustodyCount(Number(result?.custodyCount) || 0);
            })
            .catch(() => { /* não bloqueia a lista */ });
    }

    useEffect(() => {
        getUsers(search, page || 1)
            .then(result => {
                setUsers(result.rows);
                setCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });

    }, [page, search])

    useEffect(() => {
        if (showInviteCodes) {
            loadInviteCodes();
        }
    }, [inviteCodesPage, showInviteCodes])

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        setEditUser(users.find(u => u.id == id));
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        stopUser(id)
            .then(result => { window.location.reload() })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startUser(id)
            .then(result => { window.location.reload() })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message)
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        deleteUser(id)
            .then(result => { window.location.reload() })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function reloadUsers() {
        return getUsers(search, page || 1)
            .then(result => {
                setUsers(result.rows);
                setCount(result.count);
            });
    }

    function onMarkFundPaid(user) {
        if (payingFundId) return;
        const amount = Number(user.fundPayable) || 0;
        const pending = Boolean(user.fundPayout);
        if (!(amount > 0) && !pending) return;
        const ok = window.confirm(
            pending
                ? `Marcar o saque pedido de ${user.name || user.email} como pago?\nO fundo deste cliente será zerado.`
                : `Marcar o fundo de ${user.name || user.email} (${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT) como pago?`
        );
        if (!ok) return;
        setPayingFundId(user.id);
        markFundPaid(user.id)
            .then(result => {
                setNotification({ type: 'success', text: result?.message || 'Fundo marcado como pago.' });
                return reloadUsers();
            })
            .catch(err => {
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
            .finally(() => setPayingFundId(''));
    }

    function onSearchChange(event) {
        setSearch(event.target.value);
    }

    function onModalSubmit(event) {
        window.location.reload();
    }

    function loadInviteCodes() {
        getAllInviteCodes(inviteCodesPage, 10)
            .then(result => {
                setInviteCodes(result.rows);
                setInviteCodesCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    async function onGenerateInviteCode(isWildcard = false) {
        setGeneratingCode(true);
        try {
            await createInviteCode(null, isWildcard);
            setNotification({ 
                type: 'success', 
                text: isWildcard 
                    ? 'Código de convite coringa gerado com sucesso!' 
                    : 'Código de convite gerado com sucesso!' 
            });
            loadInviteCodes();
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || 'Erro ao gerar código de convite' });
        } finally {
            setGeneratingCode(false);
        }
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {
            setNotification({ type: 'success', text: 'Código copiado para a área de transferência!' });
        }).catch(() => {
            setNotification({ type: 'error', text: 'Erro ao copiar código' });
        });
    }

    async function onDeleteInviteCode(id) {
        if (!window.confirm('Tem certeza que deseja deletar este código de convite?')) {
            return;
        }
        try {
            await deleteInviteCode(id);
            setNotification({ type: 'success', text: 'Código de convite deletado com sucesso!' });
            loadInviteCodes();
        } catch (err) {
            setNotification({ type: 'error', text: err.response?.data || 'Erro ao deletar código de convite' });
        }
    }

    async function onEncerrarDolarizar() {
        if (dollarizing || resumingPredador) return;
        const who = custodyCount > 0 ? `${custodyCount} cliente(s) de custódia` : 'todos os clientes de custódia';
        const ok = window.confirm(
            `Encerrar e dolarizar ${who}?\n\n` +
            'Isso vende moedas com saldo acima de 5 USDC, encerra monitores e estratégias e pausa o Predador. ' +
            'Do lucro (carteira menos o capital depositado), 10% vai para a comissão e 10% para o fundo do cliente. ' +
            'O USDC permanece na carteira. A caça só volta no botão Retomar Predador.'
        );
        if (!ok) return;
        setDollarizing(true);
        try {
            const result = await encerrarDolarizar();
            setPredadorPaused(true);
            setNotification({
                type: 'success',
                text: result?.message
                    || `Encerrado: ${result?.soldCount || 0} venda(s), ${result?.monitorCount || 0} monitor(es). Predador pausado.`
            });
            loadPredadorStatus();
            getUsers(search, page || 1)
                .then(data => {
                    setUsers(data.rows);
                    setCount(data.count);
                })
                .catch(() => {});
        } catch (err) {
            setNotification({
                type: 'error',
                text: err.response?.data || err.message || 'Não foi possível encerrar e dolarizar.'
            });
        } finally {
            setDollarizing(false);
        }
    }

    async function onRetomarPredador() {
        if (dollarizing || resumingPredador) return;
        if (!window.confirm('Retomar o Predador? A caça automática volta para os clientes de custódia.'))
            return;
        setResumingPredador(true);
        try {
            const result = await retomarPredador();
            setPredadorPaused(false);
            setNotification({
                type: 'success',
                text: result?.message || 'Predador retomado.'
            });
            loadPredadorStatus();
        } catch (err) {
            setNotification({
                type: 'error',
                text: err.response?.data || err.message || 'Não foi possível retomar o Predador.'
            });
        } finally {
            setResumingPredador(false);
        }
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                    <h2 className="h4">Usuários</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <NewUserButton onClick={() => setEditUser({})} />
                            <button 
                                className="btn btn-primary me-2" 
                                onClick={() => {
                                    setShowInviteCodes(!showInviteCodes);
                                    if (!showInviteCodes) {
                                        loadInviteCodes();
                                    }
                                }}
                            >
                                {showInviteCodes ? 'Ocultar' : 'Ver'} Códigos de Convite
                            </button>
                            <SearchUser placeholder={search} onChange={onSearchChange} />
                        </div>
                    </div>
                </div>
                <div className="card border-0 shadow mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div>
                                <h3 className="h6 mb-1">Sinal do Bitcoin · Predador 3</h3>
                                <p className="text-muted small mb-2">
                                    {btcSignal?.reason || 'Lendo o Bitcoin no gráfico de 4 horas...'}
                                </p>
                                <div className="d-flex flex-wrap gap-2 small">
                                    <span className={`badge ${btcSignal?.emaUp ? 'bg-success' : 'bg-secondary'}`}>
                                        EMA {btcSignal?.emaUp ? 'alta' : 'baixa'}
                                    </span>
                                    <span className={`badge ${btcSignal?.macdUp ? 'bg-success' : 'bg-secondary'}`}>
                                        MACD {btcSignal?.macdUp ? 'alta' : 'baixa'}
                                    </span>
                                    <span className="badge bg-light text-dark">
                                        RSI {btcSignal?.rsi == null ? '—' : Number(btcSignal.rsi).toFixed(0)}
                                    </span>
                                </div>
                            </div>
                            <span className={`badge ${btcSignal?.ok ? 'bg-success' : 'bg-danger'}`}>
                                {btcSignal == null ? '...' : (btcSignal.ok ? 'Predador 3 liberado' : 'Predador 3 travado')}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="card border-0 shadow mb-4">
                    <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h3 className="h6 mb-1">Encerrar e dolarizar</h3>
                            <p className="text-muted small mb-0">
                                {predadorPaused
                                    ? 'Predador pausado. A caça dos clientes de custódia só volta quando você apertar Retomar Predador.'
                                    : 'Vende moedas de custódia acima de 5 USDC, encerra monitores e estratégias e pausa o Predador. 10% do lucro vai para a comissão e 10% para o fundo do cliente. O saldo em USDC fica na carteira.'}
                            </p>
                        </div>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            {predadorPaused
                                ? <span className="badge bg-warning text-dark">Predador pausado</span>
                                : null}
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={onEncerrarDolarizar}
                                disabled={dollarizing || resumingPredador}
                            >
                                {dollarizing ? 'Encerrando...' : 'Encerrar e dolarizar'}
                            </button>
                            {predadorPaused
                                ? (
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={onRetomarPredador}
                                        disabled={dollarizing || resumingPredador}
                                    >
                                        {resumingPredador ? 'Retomando...' : 'Retomar Predador'}
                                    </button>
                                )
                                : null}
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">Nome</th>
                                <th className="border-gray-200">Email</th>
                                <th className="border-gray-200">Limite</th>
                                <th className="border-gray-200">USDC Custódia</th>
                                <th className="border-gray-200">PnL</th>
                                <th className="border-gray-200">Fundo</th>
                                <th className="border-gray-200">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                users && users.length
                                    ? users.map(user => (<UserRow key={user.id} data={user} payingFund={payingFundId === user.id} onEditClick={onEditClick} onStartClick={onStartClick} onStopClick={onStopClick} onDeleteClick={onDeleteClick} onMarkFundPaid={onMarkFundPaid} />))
                                    : <></>
                            }
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                {showInviteCodes && (
                    <div className="card card-body border-0 shadow table-wrapper table-responsive mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h2 className="h5 mb-0">Códigos de Convite</h2>
                            <div className="d-flex gap-2">
                                <button 
                                    className="btn btn-primary" 
                                    onClick={() => onGenerateInviteCode(false)}
                                    disabled={generatingCode}
                                >
                                    {generatingCode ? 'Gerando...' : 'Gerar Novo Código'}
                                </button>
                                <button 
                                    className="btn btn-warning" 
                                    onClick={() => onGenerateInviteCode(true)}
                                    disabled={generatingCode}
                                    title="Código coringa pode ser usado várias vezes e não expira"
                                >
                                    {generatingCode ? 'Gerando...' : 'Gerar Código Coringa'}
                                </button>
                            </div>
                        </div>
                        <p className="text-muted mb-4">
                            Gerencie códigos de convite para novos usuários. Códigos normais podem ser usados uma vez. 
                            <strong> Códigos coringa</strong> podem ser usados várias vezes e não expiram (só vencem quando deletados).
                        </p>
                        {inviteCodes.length > 0 ? (
                            <>
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Tipo</th>
                                            <th>Criado por</th>
                                            <th>Status</th>
                                            <th>Usado por</th>
                                            <th>Data de Criação</th>
                                            <th>Data de Uso</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inviteCodes.map(code => (
                                            <tr key={code.id}>
                                                <td>
                                                    <code className="text-primary">{code.code}</code>
                                                </td>
                                                <td>
                                                    {code.isWildcard ? (
                                                        <span className="badge bg-warning text-dark">Coringa</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">Normal</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {code.creator ? code.creator.email : 'Sistema'}
                                                </td>
                                                <td>
                                                    {code.isWildcard ? (
                                                        <span className="badge bg-info">Ativo</span>
                                                    ) : code.isUsed ? (
                                                        <span className="badge bg-danger">Usado</span>
                                                    ) : (
                                                        <span className="badge bg-success">Disponível</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {code.user ? code.user.email : '-'}
                                                </td>
                                                <td>
                                                    {new Date(code.createdAt).toLocaleDateString('pt-BR')}
                                                </td>
                                                <td>
                                                    {code.usedAt ? new Date(code.usedAt).toLocaleDateString('pt-BR') : '-'}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        {(code.isWildcard || !code.isUsed) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => copyToClipboard(code.code)}
                                                                title="Copiar código"
                                                            >
                                                                <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => onDeleteInviteCode(code.id)}
                                                            title="Deletar código"
                                                        >
                                                            <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {inviteCodesCount > 10 && (
                                    <div className="d-flex justify-content-center mt-3">
                                        <button 
                                            className="btn btn-sm btn-outline-primary me-2"
                                            onClick={() => {
                                                setInviteCodesPage(Math.max(1, inviteCodesPage - 1));
                                            }}
                                            disabled={inviteCodesPage === 1}
                                        >
                                            Anterior
                                        </button>
                                        <span className="align-self-center me-2">
                                            Página {inviteCodesPage} de {Math.ceil(inviteCodesCount / 10)}
                                        </span>
                                        <button 
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => {
                                                setInviteCodesPage(inviteCodesPage + 1);
                                            }}
                                            disabled={inviteCodesPage >= Math.ceil(inviteCodesCount / 10)}
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-muted">Nenhum código de convite encontrado.</p>
                                <button className="btn btn-primary" onClick={onGenerateInviteCode} disabled={generatingCode}>
                                    {generatingCode ? 'Gerando...' : 'Gerar Primeiro Código'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                <Footer />
            </main>
            <UserModal data={editUser} onSubmit={onModalSubmit} />
            <Toast type={notification.type} text={notification.text} />
        </>
    )
}

export default Users;